import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CopyIcon, ExternalLinkIcon, InfoIcon } from "lucide-react";
import {
  Box,
  Button,
  Callout,
  Checkbox,
  Dialog,
  Flex,
  Text,
} from "@radix-ui/themes";
import toast from "react-hot-toast";
import { IntegrationDefinition } from "@/types/breeze/Integration";
import { integrationService } from "@/services/IntegrationService";
import { ApiKeyConfigForm } from "./forms/ApiKeyConfigForm";
import { OAuthConfigForm } from "./forms/OAuthConfigForm";
import { OAuthFormProvider } from "./OAuthFormContext";
import { getProviderExtension, OAuthSavePayload } from "./providers/registry";
import { getConfigShape, getDialogCopy, supportsNative } from "./shape";

type OAuthStatus = Awaited<
  ReturnType<typeof integrationService.getIntegrationOAuthSettings>
>;

interface ConfigDialogProps {
  definition: IntegrationDefinition | null;
  status?: OAuthStatus | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAfterSave: (def: IntegrationDefinition) => void;
  callbackUrl: string;
  setupGuide?: { title: string; steps: string[]; docsUrl?: string };
}

export function ConfigDialog({
  definition,
  status,
  open,
  onOpenChange,
  onAfterSave,
  callbackUrl,
  setupGuide,
}: ConfigDialogProps) {
  const queryClient = useQueryClient();

  const [nativeAudiencesText, setNativeAudiencesText] = useState("");
  const [webClientId, setWebClientId] = useState("");
  const [webClientSecret, setWebClientSecret] = useState("");
  const [hasExistingWebSecret, setHasExistingWebSecret] = useState(false);

  const [appleWebMethod, setAppleWebMethod] = useState<"p8" | "jwt">("p8");
  const [applePrivateKey, setApplePrivateKey] = useState("");
  const [hasExistingApplePrivateKey, setHasExistingApplePrivateKey] =
    useState(false);
  const [appleKeyId, setAppleKeyId] = useState("");
  const [appleTeamId, setAppleTeamId] = useState("");
  const [allowMissingEmail, setAllowMissingEmail] = useState(false);

  const [apiKey, setApiKey] = useState("");

  const [useSocialSignIn, setUseSocialSignIn] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  // Reset state on each open so a previous definition's values don't leak in.
  useEffect(() => {
    if (!open || !definition) return;
    setNativeAudiencesText((status?.native_audiences ?? []).join(", "));
    setWebClientId(status?.web_client_id ?? "");
    setWebClientSecret("");
    setHasExistingWebSecret(!!status?.has_web_client_secret);
    setApplePrivateKey("");
    setHasExistingApplePrivateKey(!!status?.has_apple_private_key);
    setAppleKeyId(status?.apple_key_id ?? "");
    setAppleTeamId(status?.apple_team_id ?? "");
    setAllowMissingEmail(!!status?.allow_missing_email);
    setAppleWebMethod(
      status?.has_apple_private_key
        ? "p8"
        : status?.has_web_client_secret
          ? "jwt"
          : "p8",
    );
    setUseSocialSignIn(
      !!definition.parent_provider && !!status?.use_social_sign_in,
    );
    setApiKey("");
    setShowGuide(false);
  }, [open, definition?.provider]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!definition) return null;

  const shape = getConfigShape(definition);
  const copy = getDialogCopy(definition);
  const ext = getProviderExtension(definition.provider);
  const showCallback = shape !== "api_key";

  const handleSave = async () => {
    try {
      if (shape === "api_key") {
        if (!apiKey.trim()) {
          toast.error("API key is required");
          return;
        }
        await integrationService.createApiKeyConnection({
          integration_definition_id: definition.id,
          name: `${definition.display_name} API key`,
          api_key: apiKey,
          is_user_connection: false,
        });
        toast.success("API key saved");
        queryClient.invalidateQueries({ queryKey: ["integration-connections"] });
        onAfterSave(definition);
        onOpenChange(false);
        return;
      }

      const formState = {
        definition,
        nativeAudiencesText,
        setNativeAudiencesText,
        webClientId,
        setWebClientId,
        webClientSecret,
        setWebClientSecret,
        hasExistingWebSecret,
        appleWebMethod,
        setAppleWebMethod,
        applePrivateKey,
        setApplePrivateKey,
        hasExistingApplePrivateKey,
        appleKeyId,
        setAppleKeyId,
        appleTeamId,
        setAppleTeamId,
        allowMissingEmail,
        setAllowMissingEmail,
      };

      const payload: OAuthSavePayload = {
        use_social_sign_in: useSocialSignIn,
        is_enabled: true,
      };

      if (!useSocialSignIn) {
        if (supportsNative(definition)) {
          payload.native_audiences = nativeAudiencesText
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
        }
        payload.web_client_id = webClientId.trim();

        const skipDefaultSecret =
          ext.shouldSkipDefaultClientSecret?.(formState) ?? false;
        if (!skipDefaultSecret && webClientSecret.trim()) {
          payload.web_client_secret = webClientSecret;
        }

        ext.buildPayloadExtras?.(formState, payload);
      }

      await integrationService.setIntegrationOAuthSettings(
        definition.provider,
        payload,
      );

      if (definition.can_social_sign_in && definition.operations.length === 0) {
        toast.success("Settings saved");
        queryClient.invalidateQueries({ queryKey: ["integration-oauth-all"] });
      } else {
        toast.success("Settings saved");
      }
      onAfterSave(definition);
      onOpenChange(false);
    } catch (error: any) {
      const msg =
        typeof error?.response?.data === "string"
          ? error.response.data
          : error?.response?.data?.error || error?.message || "Failed";
      toast.error(msg);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content style={{ maxWidth: "min(960px, 95vw)" }}>
        <Dialog.Title>{copy.title}</Dialog.Title>
        <Dialog.Description size="2" color="gray" mb="4">
          {copy.description}
        </Dialog.Description>

        <Flex direction="column" gap="3">
          {setupGuide && (
            <Box>
              <Button
                variant="ghost"
                size="1"
                onClick={() => setShowGuide(!showGuide)}
              >
                <InfoIcon size={14} />
                {showGuide ? "Hide" : "Show"} setup instructions
              </Button>
              {showGuide && (
                <Callout.Root size="1" mt="2" color="blue">
                  <Callout.Icon>
                    <InfoIcon size={14} />
                  </Callout.Icon>
                  <Callout.Text>
                    <Text
                      size="2"
                      weight="bold"
                      style={{ display: "block", marginBottom: 8 }}
                    >
                      {setupGuide.title}
                    </Text>
                    <ol style={{ margin: 0, paddingLeft: 20 }}>
                      {setupGuide.steps.map((step, i) => (
                        <li key={i} style={{ marginBottom: 4 }}>
                          {step}
                        </li>
                      ))}
                    </ol>
                    {setupGuide.docsUrl && (
                      <Box mt="2">
                        <a
                          href={setupGuide.docsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            fontSize: "var(--font-size-1)",
                            color: "var(--blue-11)",
                          }}
                        >
                          Open {definition.display_name} developer console
                        </a>
                      </Box>
                    )}
                  </Callout.Text>
                </Callout.Root>
              )}
            </Box>
          )}

          {showCallback && (
            <Box
              p="2"
              style={{
                backgroundColor: "var(--gray-3)",
                borderRadius: "4px",
              }}
            >
              <Flex justify="between" align="start">
                <Box style={{ flex: 1 }}>
                  <Text
                    size="1"
                    weight="medium"
                    color="gray"
                    style={{ display: "block", marginBottom: 4 }}
                  >
                    Web callback URL — only used by the web flow. Add it to
                    your {definition.display_name} app's redirect URIs.
                  </Text>
                  <Text
                    size="1"
                    style={{
                      fontFamily: "monospace",
                      wordBreak: "break-all",
                      display: "block",
                    }}
                  >
                    {callbackUrl}
                  </Text>
                </Box>
                <Button
                  variant="ghost"
                  size="1"
                  style={{ flexShrink: 0 }}
                  onClick={() => {
                    navigator.clipboard.writeText(callbackUrl);
                    toast.success("Copied");
                  }}
                >
                  <CopyIcon size={14} />
                </Button>
              </Flex>
            </Box>
          )}

          {definition.parent_provider && (
            <Box
              p="2"
              style={{
                backgroundColor: "var(--blue-3)",
                borderRadius: "4px",
              }}
            >
              <Flex gap="2" align="center">
                <Checkbox
                  checked={useSocialSignIn}
                  onCheckedChange={(c) => setUseSocialSignIn(c === true)}
                />
                <Text size="2">Reuse parent provider credentials</Text>
              </Flex>
            </Box>
          )}

          {!useSocialSignIn && shape === "api_key" && (
            <ApiKeyConfigForm
              definition={definition}
              apiKey={apiKey}
              onApiKeyChange={setApiKey}
            />
          )}

          {!useSocialSignIn && shape === "oauth" && (
            <OAuthFormProvider
              value={{
                definition,
                nativeAudiencesText,
                setNativeAudiencesText,
                webClientId,
                setWebClientId,
                webClientSecret,
                setWebClientSecret,
                hasExistingWebSecret,
                appleWebMethod,
                setAppleWebMethod,
                applePrivateKey,
                setApplePrivateKey,
                hasExistingApplePrivateKey,
                appleKeyId,
                setAppleKeyId,
                appleTeamId,
                setAppleTeamId,
                allowMissingEmail,
                setAllowMissingEmail,
              }}
            >
              <OAuthConfigForm />
            </OAuthFormProvider>
          )}

          <Flex gap="2" mt="2" justify="end">
            <Button variant="soft" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              <ExternalLinkIcon size={14} /> Save
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
